/**
 * WHO OWNS THE STAGE — one presenter, decided the same way by every client.
 *
 * The product has exactly one screen-share slot: `MediaSnapshot.screenShareOwnerId`
 * is a single id, `SubscriptionPlan.screenShare` is a single boolean, and
 * `PresentationLayout` has one box. That was never enforced at the PUBLISH end,
 * so two people could both start a share; each of them saw their own screen and
 * believed they were presenting, while viewers resolved a winner with
 * `roster.find((p) => p.sharing)` — ARRAY POSITION, which is local-first and
 * then whatever order that browser happens to hold `remoteParticipants` in. Two
 * viewers could therefore be looking at two different screens, and the loser
 * went on encoding and uploading ~1.6 Mbps that nobody had subscribed to.
 *
 * This module is the whole decision, and it is deliberately pure: no DOM, no
 * livekit-client, no React. Everything it reads is REPLICATED STATE — the
 * `sharing` flag the SFU already publishes, and a `shareClaimedAt` participant
 * attribute the SFU replicates to every client byte-for-byte. Two clients given
 * the same room therefore compute the same owner, every time, which is the
 * property `find()` never had.
 *
 * THE RULE, in order:
 *   1. Only participants who are actually publishing a share can own it.
 *   2. The HIGHEST claim wins. A claim is written at the moment a share starts,
 *      so "highest" means "most recently started", and a deliberate take-over is
 *      simply a new claim — there is no second mechanism to forge or lose.
 *   3. Equal claims (including two clients with no claim at all) are broken by
 *      the lexicographically smallest id. Arbitrary, but TOTAL and identical
 *      everywhere, which is the only thing that matters.
 *
 * The claim is NOT a wall clock, even though it is seeded from one: `nextClaim`
 * is a Lamport counter (`max(now, highest seen + 1)`), so a new claim beats
 * every claim it has observed no matter how far apart two machines' clocks are.
 * A laptop five minutes fast cannot win every take-over for the rest of the
 * meeting, and a laptop five minutes slow can still take the stage.
 *
 * This is NOT a security boundary. A hostile client can claim any number it
 * likes; the answer to that is the host's Stop-sharing verb, which revokes the
 * publish grant at the SFU. This decides races between honest clients.
 */

/** LiveKit participant attribute carrying the claim. Replicated by the SFU. */
export const SHARE_CLAIM_ATTRIBUTE = 'shareClaimedAt';

/**
 * The shape the arbiter needs. `MediaParticipant` satisfies it structurally, so
 * nothing has to build an adapter to ask a question about the live roster.
 */
export interface ShareClaimant {
  readonly id: string;
  readonly sharing: boolean;
  /** From `SHARE_CLAIM_ATTRIBUTE`; null when the claim never arrived. */
  readonly shareClaimedAt: number | null;
}

/** Someone whose screen is on the stage, named. */
export interface SharePresenter {
  readonly id: string;
  readonly name: string;
}

/**
 * A claim that never arrived ranks below every real one, and ties fall through
 * to the id. A publisher whose `setAttributes` failed still gets a deterministic
 * answer rather than a per-viewer one.
 */
const claimOf = (claimant: ShareClaimant): number =>
  claimant.shareClaimedAt !== null && Number.isFinite(claimant.shareClaimedAt)
    ? claimant.shareClaimedAt
    : 0;

/** Higher claim first, then smaller id. A total order over the whole roster. */
export function outranks(a: ShareClaimant, b: ShareClaimant): boolean {
  const left = claimOf(a);
  const right = claimOf(b);
  if (left !== right) return left > right;
  return a.id < b.id;
}

/**
 * THE one presenter, or null. Never array position — see the header.
 *
 * `readonly` input, no sort: this runs on every roster sync, which LiveKit fires
 * several times a second on speaker and quality events alone.
 */
export function resolveShareOwner(claimants: readonly ShareClaimant[]): string | null {
  let winner: ShareClaimant | null = null;
  for (const claimant of claimants) {
    if (!claimant.sharing) continue;
    if (winner === null || outranks(claimant, winner)) winner = claimant;
  }
  return winner?.id ?? null;
}

/**
 * The claim to write when starting or taking over a share.
 *
 * `now` only seeds it. What guarantees the new claim WINS is that it is greater
 * than every claim this client has seen, which is what makes a take-over work
 * across machines whose clocks disagree.
 *
 * EVERY claim on the roster is counted, including one on somebody who is not
 * publishing — but do not read that as the counter's memory, because it is not:
 * `stopShare` CLEARS this client's claim on the way out, and a client that
 * notices a claim of its own with no share behind it clears that too. What is
 * being counted here is the claim of somebody whose share is in flight — the
 * beat between `setAttributes` landing and `publishTrack` landing — and
 * counting it is what stops two starts a millisecond apart from minting the
 * same number. Monotonicity where it MATTERS is preserved by the live holder:
 * a take-over always races a claim that is still on the wire, so it is always
 * greater than the one it is taking the stage from.
 */
export function nextShareClaim(now: number, claimants: readonly ShareClaimant[]): number {
  let highest = 0;
  for (const claimant of claimants) highest = Math.max(highest, claimOf(claimant));
  return Math.max(Math.trunc(now), highest + 1);
}

/**
 * Is somebody ELSE holding the stage right now?
 *
 * Track-gated on purpose, and it must stay that way: this is what the STAGE
 * points at, and a stage pointing at a track that does not exist is a black
 * box with somebody's name under it.
 */
export function currentPresenter(
  claimants: readonly (ShareClaimant & { readonly name?: string })[],
  localId: string,
): SharePresenter | null {
  const ownerId = resolveShareOwner(claimants);
  if (ownerId === null || ownerId === localId) return null;
  const owner = claimants.find((claimant) => claimant.id === ownerId);
  return { id: ownerId, name: owner?.name ?? 'Someone else' };
}

/**
 * Somebody whose share is ON ITS WAY: a claim on the wire, no track behind it.
 *
 * THE WINDOW THIS EXISTS FOR is manufactured deliberately by the start
 * sequence. `livekitLocalControls` writes the claim and AWAITS it before it
 * publishes, so that the claim reaches every other client ahead of the share it
 * explains. Between those two acks — one signal round trip plus a full
 * renegotiation for a new video transceiver — a starting presenter is
 * `sharing: false` with a real claim, and a guard that only looked at `sharing`
 * saw an empty stage and let a SECOND person through. Their claim, minted a
 * moment later, is higher; they win; and the person who pressed Share first is
 * yielded out of their own share. That is the exact accident the guard's own
 * comment says it prevents, so the guard has to be able to see this.
 *
 * Not used by `resolveShareOwner`: the stage still follows tracks. This is
 * about who is ALLOWED to start, which is a different question.
 *
 * A claim that never grows a track is bounded, not permanent — the client that
 * wrote it clears it (on stop, on a failed publish, and on noticing it holds one
 * with no capture behind it), and a client that cannot do even that loses its
 * attributes when the SFU drops it. Until then a take-over is one press away,
 * which is the same answer this file gives for every other way to be blocked.
 */
export function pendingPresenter(
  claimants: readonly (ShareClaimant & { readonly name?: string })[],
  localId: string,
): SharePresenter | null {
  let winner: (ShareClaimant & { readonly name?: string }) | null = null;
  for (const claimant of claimants) {
    if (claimant.sharing || claimant.id === localId) continue;
    if (claimOf(claimant) === 0) continue;
    if (winner === null || outranks(claimant, winner)) winner = claimant;
  }
  if (winner === null) return null;
  /*
   * A CLAIM IN FLIGHT ONLY BLOCKS SOMEBODY IT WOULD BEAT.
   *
   * The loser of a race that has already been decided is still `sharing: false`
   * with a real claim on the wire for as long as its own client takes to notice
   * and withdraw it. `currentPresenter` answers `null` for the client that OWNS
   * the stage - correctly, it is not somebody ELSE - and the fall-through then
   * refused that client's next Share BY THE NAME OF SOMEBODY WHO HAD ALREADY
   * LOST, over a stage that is already theirs. The `claimant.sharing` skip
   * above closes that only once the loser is live; one state earlier, while
   * their claim is still in flight, it does not.
   *
   * Gated on the local claimant actually HOLDING the stage, not merely on
   * holding a bigger number: a claim of my own with nothing behind it is a
   * phantom (`holdsPhantomClaim` withdraws it) and must never be worth a
   * veto over somebody whose share is genuinely on its way.
   */
  const local = claimants.find((claimant) => claimant.id === localId);
  if (local !== undefined && local.sharing && outranks(local, winner)) return null;
  return { id: winner.id, name: winner.name ?? 'Someone else' };
}

/**
 * WHO STOPS THIS CLIENT FROM STARTING A SHARE — a live presenter first, and
 * failing that a share that is already on its way.
 *
 * The order matters: a live share always outranks a claim in flight, so the
 * name in the refusal is the name of the screen the user can actually see.
 */
export function blockingPresenter(
  claimants: readonly (ShareClaimant & { readonly name?: string })[],
  localId: string,
): SharePresenter | null {
  return currentPresenter(claimants, localId) ?? pendingPresenter(claimants, localId);
}

/**
 * A CLAIM OF MY OWN WITH NOTHING BEHIND IT — the phantom this client must clear.
 *
 * `pendingPresenter` blocks other people on a claim in flight, which is only
 * safe because a claim that stops being in flight stops existing. The clear in
 * `stopShare` is best-effort (it can be refused by a reconnecting room), and a
 * publish can fail after the claim has landed. So every client re-checks its own
 * claim on every roster sync: if I hold one, I am not publishing, no capture is
 * running and no start is in flight, the claim is rubbish and I withdraw it.
 * Nobody else can do this — only this client knows whether its own start is
 * still running.
 */
export function holdsPhantomClaim(
  claimants: readonly ShareClaimant[],
  localId: string,
  capturing: boolean,
  starting: boolean,
): boolean {
  if (capturing || starting) return false;
  const local = claimants.find((claimant) => claimant.id === localId);
  if (local === undefined) return false;
  return !local.sharing && claimOf(local) > 0;
}

/**
 * MY share lost, and must stop.
 *
 * The one honest race the guard cannot prevent is two people pressing Share in
 * the same instant, each seeing an empty stage. Both publish, both claims land,
 * and every client — including both presenters — agrees on the winner a moment
 * later. This is what the loser's client reads to release its capture instead of
 * uploading into a void, and it is why nobody has to be asked to sort it out.
 */
export function mustYieldShare(claimants: readonly ShareClaimant[], localId: string): boolean {
  const local = claimants.find((claimant) => claimant.id === localId);
  if (local === undefined || !local.sharing) return false;
  return resolveShareOwner(claimants) !== localId;
}

/** Attribute string → claim. Anything that is not a positive integer is no claim. */
export function parseShareClaim(raw: string | undefined): number | null {
  if (raw === undefined || raw.length === 0) return null;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Claim → attribute string. The empty string is how LiveKit clears one. */
export const formatShareClaim = (claim: number | null): string =>
  claim === null ? '' : String(Math.trunc(claim));
